import * as api from "../../../../../Utils/apis/professionalContactsApi";
import { FIRM_TYPES } from "../../../../../domain/professionalContacts/professionalContacts.types";
import {
  ExecuteSqlStoreProc,
  buildBorrowerOptionSetObject,
} from "../../../Molecules/Common/customForms/Add_Borrower/Borrower_Form_helper";

export const ALL_FIRMS_TAB = "all";
export const AUDIT_TAB = "audit";

export const PROFESSIONAL_DETAILS_LOAN_TEMPLATE_ID =
  "3e130b27-051f-4012-84cf-6ab438aa6441";

export const FILTER_CHIPS = [
  { key: "active", label: "Active" },
  { key: "panel", label: "Panel Approved" },
  { key: "piiExpiring", label: "PII Expiring" },
  { key: "distressed", label: "Distressed" },
  { key: "noFurtherInstructions", label: "No Further Instructions" },
];

export function buildFirmQueryParams(tab, searchText, activeChipKeys) {
  const params = { page: 1, pageSize: 100 };
  if (tab && tab !== ALL_FIRMS_TAB) params.type = tab;
  if (searchText) params.q = searchText;
  FILTER_CHIPS.forEach(({ key }) => {
    if (activeChipKeys.has(key)) params[key] = true;
  });
  return params;
}

export async function fetchFirmCount(tab) {
  const page = await api.listFirms(tab === ALL_FIRMS_TAB ? {} : { type: tab });
  return page?.total ?? page?.items?.length ?? 0;
}

export async function fetchTabCounts() {
  const [allPage, ...typePages] = await Promise.all([
    api.listFirms({}),
    ...FIRM_TYPES.map((type) => api.listFirms({ type })),
  ]);
  const counts = { [ALL_FIRMS_TAB]: allPage?.total ?? 0 };
  FIRM_TYPES.forEach((type, index) => {
    counts[type] = typePages[index]?.total ?? 0;
  });
  return counts;
}

/**
 * How a bank account's creditor bank details were obtained: "Lookup" when the BankFinder
 * sort-code lookup returned a bank, "Manual" when it did not answer and the user typed them.
 */
export const BANK_DETAILS_SOURCE = { LOOKUP: "Lookup", MANUAL: "Manual" };

/** Creditor bank fields the API locks when it reports bankDetailsLocked without naming them. */
export const DEFAULT_LOCKED_BANK_FIELDS = [
  "bankName",
  "town",
  "postcode",
  "country",
];

/**
 * Strips the row-tracking key and the server-owned lock state (bankDetailsLocked/lockedFields,
 * which GET /professional-firms/{firmId} returns but the upsert must not echo back), and always
 * states how the bank details were obtained — defaulting to Manual for a row no lookup filled.
 * @param {object} bankAccount
 */
const BANK_ACCOUNT_RESPONSE_ONLY_KEYS = [
  "_key",
  "bankDetailsLocked",
  "lockedFields",
];

const FIRM_RESPONSE_ONLY_KEYS = [
  "id",
  "createdUtc",
  "modifiedUtc",
  "deletedContacts",
  "deletedAddresses",
  "deletedBankAccounts",
];

const omitKeys = (source, keys) => {
  const result = { ...source };
  keys.forEach((key) => delete result[key]);
  return result;
};

export function toBankAccountUpsert(bankAccount) {
  const upsert = omitKeys(bankAccount || {}, BANK_ACCOUNT_RESPONSE_ONLY_KEYS);
  return {
    ...upsert,
    bankDetailsSource: upsert.bankDetailsSource || BANK_DETAILS_SOURCE.MANUAL,
  };
}

export function toFirmUpsert(firm) {
  const upsert = omitKeys(firm, FIRM_RESPONSE_ONLY_KEYS);
  return {
    ...upsert,
    bankAccounts: (upsert.bankAccounts || []).map(toBankAccountUpsert),
  };
}

export async function fetchBankOptionSets() {
  const categories = "BankType,Country";
  try {
    const response = await ExecuteSqlStoreProc([
      {
        id: "BankAccountDropdowns",
        spName: "[DF].[usp_GetOptionSetsByCategories]",
        parameters: { Categories: categories },
      },
    ]);
    const optionSets = buildBorrowerOptionSetObject(
      categories.split(","),
      response?.BankAccountDropdowns || []
    );
    return {
      bankType: optionSets.BankType || [],
      country: optionSets.Country || [],
    };
  } catch (err) {
    return { bankType: [], country: [] };
  }
}

export function buildAssociatedLoansQueryParams(state = {}) {
  const params = {
    page: state.page || 1,
    pageSize: state.pageSize || 25,
    sort: state.sort || "loanName",
    sortDir: state.sortDir || "asc",
  };
  if (state.q) params.q = state.q;
  if (state.stage) params.stage = state.stage;
  if (state.status) params.status = state.status;
  if (state.role) params.role = state.role;
  return params;
}

/** FIRM-DOCUMENTS-032 (v1.12.0): sibling to buildAssociatedLoansQueryParams, for GET .../documents. */
export function buildFirmDocumentsQueryParams(state = {}) {
  const params = {
    page: state.page || 1,
    pageSize: state.pageSize || 25,
    sort: state.sort || "uploadedUtc",
    sortDir: state.sortDir || "desc",
  };
  if (state.q) params.q = state.q;
  return params;
}

export { api };
